import type { LogEntry, LoggerOptions } from '@tbd/lift-logger';
import { formatters, Logger } from '@tbd/lift-logger';

export { MONOREPO_VERSION as TBD_VERSION } from './generated-version.ts';
// Type definitions
export type DeploymentEnvironment =
  | 'local'
  | 'dev'
  | 'preview'
  | 'staging'
  | 'production';

// #region Application constants
export const LIFT_PARTICIPANT_EMAIL_DOMAIN = 'lift-students.email';
// #endregion Application constants

// Constants for domains
export const CLOUDFLARE_ACCOUNT_NAME = 'laerdal-labs' as const;
export const CLOUDFLARE_ACCOUNT_ID =
  'dc214760a33ef942ace30537c047d124' as const;
export const CLOUDFLARE_PAGES_APEX = 'pages.dev' as const;
export const CLOUDFLARE_WORKERS_APEX = 'workers.dev' as const;

export const ASSESSMENTS_DEV_APEX = 'laerdal-lift-dev.com' as const;
export const ASSESSMENTS_UAT_APEX = 'laerdal-lift-uat.com' as const;
export const ASSESSMENTS_PROD_APEX = 'laerdal-lift.com' as const;
export const ASSESSMENTS_EXTRA_APEX = 'laerdal-lift-momentum.com' as const;

export const PORTAL_PROD_APEX = 'lift.training' as const;
export const PORTAL_DEV_APEX = 'lift-dev.training' as const;
export const PORTAL_STAGING_APEX = 'lift-staging.training' as const;
export const PORTAL_PREVIEW_APEX = 'lift-preview.training' as const;

export const LIFT_APEXES = [
  ASSESSMENTS_DEV_APEX,
  ASSESSMENTS_UAT_APEX,
  ASSESSMENTS_PROD_APEX,
  ASSESSMENTS_EXTRA_APEX,
  PORTAL_PROD_APEX,
  PORTAL_STAGING_APEX,
  PORTAL_PREVIEW_APEX,
  PORTAL_DEV_APEX,
  CLOUDFLARE_PAGES_APEX,
  CLOUDFLARE_WORKERS_APEX,
] as const;

export const APP_NAMES = {
  PORTAL: 'portal',
  ASSESSMENTS: 'assessments',
  PRACTICE: 'practice',
} as const;

export type AppName = (typeof APP_NAMES)[keyof typeof APP_NAMES];

export const CLOUDFLARE_PROJECT_NAMES = {
  [APP_NAMES.PORTAL]: 'lift-portal',
  [APP_NAMES.ASSESSMENTS]: 'lift-assessments-assets',
  [APP_NAMES.PRACTICE]: 'lift-practice-vue',
} as const;
export type ProjectName =
  (typeof CLOUDFLARE_PROJECT_NAMES)[keyof typeof CLOUDFLARE_PROJECT_NAMES];

export function projectNameToAppName(projectName: string): AppName | undefined {
  return Object.entries(CLOUDFLARE_PROJECT_NAMES).find(
    ([, name]) => name === projectName
  )?.[0] as AppName | undefined;
}
export const APP_ORIGIN_HOSTNAMES = {
  [APP_NAMES.PORTAL]: `${CLOUDFLARE_PROJECT_NAMES[APP_NAMES.PORTAL]}.${CLOUDFLARE_PAGES_APEX}`,
  [APP_NAMES.ASSESSMENTS]: `${CLOUDFLARE_PROJECT_NAMES[APP_NAMES.ASSESSMENTS]}.${CLOUDFLARE_ACCOUNT_NAME}.${CLOUDFLARE_WORKERS_APEX}`,
  [APP_NAMES.PRACTICE]: `${CLOUDFLARE_PROJECT_NAMES[APP_NAMES.PRACTICE]}.${CLOUDFLARE_PAGES_APEX}`,
} as const;

const APP_NAME_LAST_PREVIEW_SUBDOMAIN_REGEX = new RegExp(
  `(?<prefix>.+)-(?<appname>${APP_NAMES.PORTAL}|${APP_NAMES.ASSESSMENTS}|${APP_NAMES.PRACTICE})$`
);

const PROJECT_NAME_FIRST_PREVIEW_SUBDOMAIN_REGEX = new RegExp(
  `^(?<appname>${CLOUDFLARE_PROJECT_NAMES[APP_NAMES.PORTAL]}|${CLOUDFLARE_PROJECT_NAMES[APP_NAMES.ASSESSMENTS]}|${CLOUDFLARE_PROJECT_NAMES[APP_NAMES.PRACTICE]})-(?<prefix>.+)`
);

// eventually this will be imported from an internationalization / localization file
export const AppTitles = {
  [APP_NAMES.PORTAL]: 'LIFT Portal',
  [APP_NAMES.ASSESSMENTS]: 'LIFT Assessments',
  [APP_NAMES.PRACTICE]: 'LIFT Practice',
} as const;
export type AppTitle = (typeof AppTitles)[keyof typeof AppTitles];

type StackKey = AppName | 'otel_collector_origin';
type OriginStack = Record<StackKey, string>;
const DEV_OTEL_COLLECTOR = 'https://otel-collector-dev.laerdal-apps-dev.com';
// const DEV_OTEL_COLLECTOR = 'https://webhook.site/84c6ff9b-a61b-4a52-9a07-5c1d43db5e51';
const PROD_OTEL_COLLECTOR =
  'https://otel-collector-production.eu-central-1.laerdal-apps.com';
const ORIGIN_STACKS: Record<DeploymentEnvironment, OriginStack> = {
  local: {
    portal: 'http://localhost:5173',
    assessments: 'http://localhost:5174',
    practice: 'http://localhost:5175',
    otel_collector_origin: DEV_OTEL_COLLECTOR,
  },
  dev: {
    portal: `https://${PORTAL_DEV_APEX}`,
    assessments: `https://${APP_NAMES.ASSESSMENTS}.${PORTAL_DEV_APEX}`,
    practice: `https://${APP_NAMES.PRACTICE}.${PORTAL_DEV_APEX}`,
    otel_collector_origin: DEV_OTEL_COLLECTOR,
  },
  preview: {
    portal: `https://${PORTAL_PREVIEW_APEX}`,
    assessments: `https://${APP_NAMES.ASSESSMENTS}.${PORTAL_PREVIEW_APEX}`,
    practice: `https://${APP_NAMES.PRACTICE}.${PORTAL_PREVIEW_APEX}`,
    otel_collector_origin: DEV_OTEL_COLLECTOR,
  },
  staging: {
    portal: `https://${PORTAL_STAGING_APEX}`,
    assessments: `https://${APP_NAMES.ASSESSMENTS}.${PORTAL_STAGING_APEX}`,
    practice: `https://${APP_NAMES.PRACTICE}.${PORTAL_STAGING_APEX}`,
    otel_collector_origin: DEV_OTEL_COLLECTOR,
  },
  production: {
    portal: `https://${PORTAL_PROD_APEX}`,
    assessments: `https://${APP_NAMES.ASSESSMENTS}.${PORTAL_PROD_APEX}`,
    practice: `https://${APP_NAMES.PRACTICE}.${PORTAL_PROD_APEX}`,
    otel_collector_origin: PROD_OTEL_COLLECTOR,
  },
} as const;

export const ABSOLUTE_URL_REGEX = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i;
export function isAbsoluteUrl(value: string) {
  return ABSOLUTE_URL_REGEX.test(value);
}

const EMAIL_REGEX = /^\S+@\S+\.\S+$/;
export function isEmail(value: string) {
  return EMAIL_REGEX.test(value);
}

export function isSafeRedirect(value: string) {
  const isAbsolute = isAbsoluteUrl(value);
  if (!isAbsolute) {
    return true;
  }
  const url = new URL(value);
  return isLocalhost(url) || isLiftUrl(url);
}

// #region User Roles

export const TFA_REQUIRED_ROLES = {
  LGH_SUPPORT_STAFF: 'LGH Support Staff',
  PROJECT_ADMIN: 'Project Admin',
} as const;

/**
 * Roles that can view Portal and manage users.
 */
export const ELEVATED_ROLE_NAMES = {
  ...TFA_REQUIRED_ROLES,
  GROUP_ADMIN: 'Group Admin',
  SITE_ADMIN: 'Site Admin',
} as const;

export const ROLE_NAMES = {
  ...ELEVATED_ROLE_NAMES,
  DATA_UPLOADER: 'Data Uploader',
  PARTICIPANT: 'Participant',
} as const;

export const ROLE_RANKS = {
  [ROLE_NAMES.LGH_SUPPORT_STAFF]: 5,
  [ROLE_NAMES.PROJECT_ADMIN]: 4,
  [ROLE_NAMES.GROUP_ADMIN]: 3,
  [ROLE_NAMES.SITE_ADMIN]: 2,
  [ROLE_NAMES.DATA_UPLOADER]: 1,
  [ROLE_NAMES.PARTICIPANT]: 0,
} as const;

export type RoleName = (typeof ROLE_NAMES)[keyof typeof ROLE_NAMES];
export type ElevatedRoleName =
  (typeof ELEVATED_ROLE_NAMES)[keyof typeof ELEVATED_ROLE_NAMES];
export type TfaRequiredRoleName =
  (typeof TFA_REQUIRED_ROLES)[keyof typeof TFA_REQUIRED_ROLES];
export type ParticipantRoleName = Extract<RoleName, 'Participant'>;

export function isRoleName(maybeRoleName: unknown): maybeRoleName is RoleName {
  return (
    typeof maybeRoleName === 'string' &&
    // @ts-expect-error - Object.values(ROLE_NAMES) is RoleName[] but maybeRoleName is only a string. But that's the point of this check, to see if maybeRoleName is a RoleName
    Object.values(ROLE_NAMES).includes(maybeRoleName)
  );
}

export function isTfaRequiredRole(
  maybeTfaRequiredRole: RoleName
): maybeTfaRequiredRole is TfaRequiredRoleName {
  return (
    // @ts-expect-error - Purpose of this function is to check the type
    Object.values(TFA_REQUIRED_ROLES).includes(maybeTfaRequiredRole)
  );
}

function isLivebornRequiredRoleToUpdate(targetRole: RoleName) {
  return (
    isElevatedRoleName(targetRole) || targetRole === ROLE_NAMES.DATA_UPLOADER
  );
}

export function isElevatedRoleName(
  maybeElevatedRoleName: RoleName
): maybeElevatedRoleName is ElevatedRoleName {
  return (
    // @ts-expect-error - Purpose of this function is to check the type
    Object.values(ELEVATED_ROLE_NAMES).includes(maybeElevatedRoleName)
  );
}

export function assertRoleName(
  maybeRoleName: unknown
): asserts maybeRoleName is RoleName {
  if (!isRoleName(maybeRoleName)) {
    throw new Error(`Expected RoleName, got ${typeof maybeRoleName}`);
  }
}

export function assertElevatedRoleName(
  maybeElevatedRoleName: RoleName
): asserts maybeElevatedRoleName is ElevatedRoleName {
  if (!isElevatedRoleName(maybeElevatedRoleName)) {
    throw new Error(
      `Expected ElevatedRoleName, got ${typeof maybeElevatedRoleName}`
    );
  }
}

export function canCreateTargetUser(
  loggedInUserRole: RoleName,
  targetRole: RoleName
) {
  return (
    isElevatedRoleName(loggedInUserRole) &&
    ROLE_RANKS[loggedInUserRole] > ROLE_RANKS[targetRole] &&
    // Liveborn rpc requires a TFA-verified auth token in order to create or edit elevated roles.
    (!isLivebornRequiredRoleToUpdate(targetRole) ||
      isTfaRequiredRole(loggedInUserRole))
  );
}
// #endregion User Roles

// Helper functions
export function isLaerdalWorker(url: { hostname: string }) {
  return url.hostname.endsWith(
    `.${CLOUDFLARE_ACCOUNT_NAME}.${CLOUDFLARE_WORKERS_APEX}`
  );
}

export function isLiftUrl(maybeUrl: { hostname: string }): boolean {
  return LIFT_APEXES.some(
    (domain) =>
      maybeUrl.hostname === domain || maybeUrl.hostname.endsWith(`.${domain}`)
  );
}

export function isCloudflarePages(maybeUrl: { hostname: string }): boolean {
  return maybeUrl.hostname.endsWith(`.${CLOUDFLARE_PAGES_APEX}`);
}

export function isLocalhost(maybeUrl: { hostname: string }): boolean {
  return ['localhost', '127.0.0.1', ''].includes(maybeUrl.hostname);
}

// only works for our simple whatever.com apex domains
export function getApexHost(maybeUrl: { hostname: string }): string {
  return maybeUrl.hostname.split('.').slice(-2).join('.');
}

export function getRootHost(maybeUrl: { href: string }): string {
  const { hostname } = new URL(maybeUrl.href);
  if (isLiftUrl({ hostname })) {
    return getApexHost({ hostname });
  }
  return hostname;
}

export function getClientHost({
  subdomain,
  href,
}: {
  subdomain?: string;
  href: string;
}): string {
  const rootHost = getRootHost({ href });

  return isLiftUrl({ hostname: rootHost })
    ? `${subdomain}.${rootHost}`
    : new URL(href).host;
}

export function getClientOrigin({
  subdomain,
  href,
}: {
  subdomain?: string;
  href: string;
}): string {
  const host = getClientHost({ subdomain, href });
  const protocol = new URL(href).protocol;
  return `${protocol}//${host}`;
}

export function getOriginStackForPrefix(prefix: string): OriginStack {
  return {
    portal: `https://${getPreviewBranchHostname(prefix, APP_NAMES.PORTAL)}`,
    assessments: `https://${getPreviewBranchHostname(prefix, APP_NAMES.ASSESSMENTS)}`,
    practice: `https://${getPreviewBranchHostname(prefix, APP_NAMES.PRACTICE)}`,
    otel_collector_origin: DEV_OTEL_COLLECTOR,
  };
}

export function getPreviewOrigins(options: { hostname: string }) {
  if (options.hostname.endsWith('.' + PORTAL_PREVIEW_APEX)) {
    const [subdomain] = options.hostname.split('.');
    if (subdomain) {
      const appNameLastResults = subdomain.match(
        APP_NAME_LAST_PREVIEW_SUBDOMAIN_REGEX
      );
      if (appNameLastResults?.groups?.prefix) {
        return getOriginStackForPrefix(appNameLastResults.groups.prefix);
      }
      const projectNameFirstResults = subdomain.match(
        PROJECT_NAME_FIRST_PREVIEW_SUBDOMAIN_REGEX
      );
      if (projectNameFirstResults?.groups?.prefix) {
        return getOriginStackForPrefix(projectNameFirstResults.groups.prefix);
      }
    }
  }
  return ORIGIN_STACKS.preview;
}

export function getPreviewBranchHostname(branch: string, appName: AppName) {
  if (appName === APP_NAMES.ASSESSMENTS) {
    const projectName = CLOUDFLARE_PROJECT_NAMES[appName];
    const hostname = `${projectName}-${branch}.${PORTAL_PREVIEW_APEX}` as const;
    return hostname;
  }

  const hostname = `${branch}-${appName}.${PORTAL_PREVIEW_APEX}` as const;
  return hostname;
}

export function getOriginBranchHostname(branch: string, appName: AppName) {
  return `${branch}.${APP_ORIGIN_HOSTNAMES[appName]}`;
}

export function getOrigins(options: { hostname: string }): OriginStack {
  const deploymentEnvironment = getDeploymentEnvironment(options);
  if (deploymentEnvironment === 'preview') {
    return getPreviewOrigins(options);
  }

  return ORIGIN_STACKS[deploymentEnvironment];
}

export type ParsedUrl = {
  href: string;
  origin: string;
  protocol: string;
  username: string;
  password: string;
  host: string;
  hostname: string;
  port: string;
  pathname: string;
  search: string;
  hash: string;
};

export function getParsedUrl(maybeUrl: URL | string): ParsedUrl {
  const url = typeof maybeUrl === 'string' ? new URL(maybeUrl) : maybeUrl;
  return {
    href: url.href,
    origin: url.origin,
    protocol: url.protocol,
    username: url.username,
    password: url.password,
    host: url.host,
    hostname: url.hostname,
    port: url.port,
    pathname: url.pathname,
    search: url.search,
    hash: url.hash,
  };
}

// Environment detection
export function getDeploymentEnvironment(maybeUrl: {
  hostname: string;
}): DeploymentEnvironment {
  const hostname = maybeUrl.hostname;
  if (isLocalhost({ hostname })) {
    return 'local';
  } else if (
    isCloudflarePages({ hostname }) ||
    hostname === PORTAL_PREVIEW_APEX ||
    hostname.endsWith(`.${PORTAL_PREVIEW_APEX}`)
  ) {
    return 'preview';
  } else if (
    hostname === PORTAL_PROD_APEX ||
    hostname.endsWith(`.${PORTAL_PROD_APEX}`)
  ) {
    return 'production';
  } else if (
    hostname === PORTAL_STAGING_APEX ||
    hostname.endsWith(`.${PORTAL_STAGING_APEX}`)
  ) {
    return 'staging';
  } else if (
    hostname === PORTAL_DEV_APEX ||
    hostname.endsWith(`.${PORTAL_DEV_APEX}`)
  ) {
    return 'dev';
  }

  console.warn(
    `Unrecognized hostname: ${hostname}. Defaulting to 'local' environment.`
  );
  return 'local';
}

// Cookie domain helper
export function getCookieDomain(maybeUrl: { href: string }): string {
  const rootHost = getRootHost({ href: maybeUrl.href });

  if (isLocalhost({ hostname: rootHost })) {
    return ''; // Use default domain for localhost
  }

  return `.${rootHost}`; // Add leading dot for subdomain support
}

export const COOKIE_NAME_SAPCDC_ASSERTION = 'gigyaAssertion' as const;
export const COOKIE_NAME_SAPCDC_DEVICE_ID = 'gigyaDeviceID' as const;
export const COOKIE_NAME_SAPCDC_UID = 'gigyaUID' as const;
export const COOKIE_NAME_SAPCDC_REG_TOKEN = 'regToken' as const;
export const COOKIE_NAME_SAPCDC_SCT_TOKEN = 'sctToken' as const;
export const COOKIE_NAME_SAPCDC_TFA_TOKEN = 'sapCdcTFAToken' as const;
export const COOKIE_NAME_SAPCDC_TOKEN = 'sapCdcToken' as const;
export const COOKIE_NAME_SC_AUTH_TOKEN = 'scAuthToken' as const;
export const COOKIE_NAME_SC_CLIENT_ID = 'scClientId' as const;
export const COOKIE_NAME_SC_CLUSTER_ID = 'scClusterId' as const;
export const COOKIE_NAME_SC_CLUSTER_TOKEN = 'scClusterToken' as const;
export const COOKIE_NAME_SC_USER_ID = 'scUserId' as const;
export const COOKIE_NAME_TZ_NAME = 'tzName' as const;

// TODO: Update other files to import this function
export function getCookieLookup(
  cookieHeader?: string | null
): Record<string, string> {
  if (!cookieHeader) return {};
  return Object.fromEntries(
    cookieHeader.split(/;\s*/).map((pair) => pair.split('='))
  );
}

export const BULK_IMPORT_STATUS = {
  BULK_IMPORT_IN_PROGRESS: 'BULK_IMPORT_IN_PROGRESS',
  BULK_IMPORT_FAILED: 'BULK_IMPORT_FAILED',
  BULK_IMPORT_PARTIAL_COMPLETE: 'BULK_IMPORT_PARTIAL_COMPLETE',
  BULK_IMPORT_COMPLETE: 'BULK_IMPORT_COMPLETE',
  BULK_IMPORT_RESET: 'BULK_IMPORT_RESET',
  BULK_IMPORT_CLEAR_ALL: 'BULK_IMPORT_CLEAR_ALL',
} as const;
export type BulkImportStatus =
  (typeof BULK_IMPORT_STATUS)[keyof typeof BULK_IMPORT_STATUS];

export function isBulkImportStatus(maybe: unknown): maybe is BulkImportStatus {
  if (typeof maybe !== 'string') return false;
  return Object.values(BULK_IMPORT_STATUS).includes(maybe as BulkImportStatus);
}

export function createLogger(
  oneOrMoreCategories: string | string[],
  options: LoggerOptions = {}
) {
  const defaults: LoggerOptions = {
    minLevel: 'debug',
    format: (entry: LogEntry) => {
      const formatter =
        typeof document === 'undefined' // on a "server"
          ? formatters['json-pretty']
          : formatters['text'];
      return formatter(entry);
    },
  };
  return new Logger(oneOrMoreCategories, Object.assign({}, defaults, options));
}

export const USER_AUTHENTICATION_STATES = {
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  AUTHENTICATED: 'AUTHENTICATED',
  PENDING_TFA_REGISTRATION: 'PENDING_TFA_REGISTRATION',
  PENDING_TFA_VERIFICATION: 'PENDING_TFA_VERIFICATION',
} as const;
